import { useState } from "react";
import { RANK_LABELS } from "../quizLogic";

export function stripEmoji(s) {
  return String(s).replace(/^[^\p{L}\p{N}]+/u, "").trim();
}

export function descParagraphs(text) {
  if (!text) return [];
  return String(text).split(/(?<=[.!?])\s+(?=[A-Z])/).map((s) => s.trim()).filter(Boolean);
}

function firstValue(arr) {
  return arr && arr[0] ? stripEmoji(arr[0]) : "";
}

function tierClass(tier) {
  return "tier-" + String(tier).replace(/\s+/g, "-");
}

function MetaRow({ label, value }) {
  return (
    <div className="meta-row">
      <span className="meta-label">{label}</span>
      <span className="meta-value">{value}</span>
    </div>
  );
}

function StatRow({ label, score }) {
  // Display clamp 8%–90% so no bar ever looks empty or maxed out.
  // The real score and the % Match badge are untouched.
  const pct = score === null || score === undefined ? 0 : score;
  const displayPct = Math.min(90, Math.max(pct, 8));
  return (
    <div className="stat">
      {label}{" "}
      <span className="bar"><i style={{ width: `${displayPct}%` }}></i></span>
    </div>
  );
}

export function MatchCard({ d, scored, rankNum, showRibbon = true, isFav, onToggleFav, isOwned, onToggleOwned, collapsible, onCtaClick }) {
  const [expanded, setExpanded] = useState(false);
  // Results-context cards collapse to 2 lines; collapsible can be forced (favorites view).
  const isCollapsible = collapsible !== undefined ? collapsible : !!rankNum;
  const showPurchase = d.purchase_signal &&
    (d.purchase_signal.includes("bought") || d.purchase_signal.includes("Best Seller"));
  return (
    <article className="match-card" data-testid={`match-card-${d.name}`}>
      {rankNum && showRibbon ? (
        <div className={`rank-ribbon rank-${rankNum}`} data-testid={`rank-ribbon-${rankNum}`}>
          {RANK_LABELS[rankNum - 1] || `MATCH No. ${rankNum}`}
        </div>
      ) : null}
      <button className="fav-heart" type="button" data-testid={`fav-heart-${d.name}`}
        aria-pressed={isFav} aria-label="Save to favorites" onClick={() => onToggleFav(d.name)}>
        {isFav ? "♥" : "♡"}
      </button>
      <div className="match-top">
        <div>
          <div className="match-name">{d.name}</div>
          <div className="match-author">{d.author || "artist unlisted"}</div>
        </div>
        <div className={`tier-chip ${tierClass(d.recognition)}`}>{d.recognition}</div>
      </div>
      {d.curator_pick ? <div className="curator-badge">★ Curator's Pick</div> : null}
      <div className={`match-desc ${isCollapsible && !expanded ? "is-collapsed" : ""}`}>
        {descParagraphs(d.description).map((s, i) => <p key={i}>{s}</p>)}
      </div>
      {isCollapsible ? (
        <button className="desc-toggle" type="button" data-testid={`desc-toggle-${d.name}`}
          aria-expanded={expanded} onClick={() => setExpanded((v) => !v)}>
          {expanded ? "− SEE LESS" : "+ SEE MORE"}
        </button>
      ) : null}
      {(!isCollapsible || expanded) && (
        <div className="match-meta">
          {firstValue(d.wisdom_tradition) ? <MetaRow label="Energy" value={firstValue(d.wisdom_tradition)} /> : null}
          {firstValue(d.need) ? <MetaRow label="Need" value={firstValue(d.need)} /> : null}
          {firstValue(d.flavor) ? <MetaRow label="Vibe" value={firstValue(d.flavor)} /> : null}
          {firstValue(d.art_style) ? <MetaRow label="Aesthetics" value={firstValue(d.art_style)} /> : null}
        </div>
      )}
      {scored ? (
        <div className="match-stats">
          {scored.total !== null && scored.total !== undefined ? (
            <span className="match-pct" data-testid={`match-pct-${d.name}`}>
              {Math.min(100, Math.round(scored.total))}% Match
            </span>
          ) : null}
          <StatRow label="energy" score={scored.energy} />
          <StatRow label="need" score={scored.need} />
          <StatRow label="vibe" score={scored.flavor} />
          <StatRow label="aesthetics" score={scored.aesthetic} />
        </div>
      ) : null}
      {showPurchase ? <div className="purchase-signal">✦ {d.purchase_signal}</div> : null}
      {onToggleOwned ? (
        <div className="own-row">
          <span className="own-label">Already have this deck?</span>
          <button className="own-toggle" type="button" data-testid={`own-toggle-${d.name}`}
            aria-pressed={!!isOwned} onClick={() => onToggleOwned(d.name)}>
            {isOwned ? "✓ Owned" : "Mark as Owned"}
          </button>
        </div>
      ) : null}
      <div className="match-foot">
        {d.link ? (
          <a className="cta" data-testid={`deck-cta-${rankNum || d.name}`} href={d.link}
            target="_blank" rel="noopener"
            onClick={() => onCtaClick && onCtaClick(d, rankNum)}>Meet Your BFF →</a>
        ) : null}
      </div>
    </article>
  );
}

export function AccessoryCard({ a, isFav, onToggleFav }) {
  return (
    <article className="accessory-card" data-testid={`accessory-card-${a.name}`}>
      <button className="fav-heart" type="button" data-testid={`fav-heart-${a.name}`}
        aria-pressed={isFav} aria-label="Save to favorites" onClick={() => onToggleFav(a.name)}>
        {isFav ? "♥" : "♡"}
      </button>
      <div className="accessory-kind">{a.kind === "book" ? "Companion Book" : "Accessory"}</div>
      <div className="accessory-name">{a.name}</div>
      <p className="accessory-desc">{a.description}</p>
      {a.link ? (
        <a className="cta" href={a.link} target="_blank" rel="noopener">Check It Out! →</a>
      ) : null}
    </article>
  );
}
